import ApiClient from '../../Shared/Services/ApiClient';
import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';
import {
  deleteApi,
  getApi,
  postApi,
  putApi,
} from '../../Shared/Services/RestService';
import {
  BackupConfig,
  BackupConfigRequest,
  BackupJob,
  BackupJobFilters,
  FolderStatus,
  JenkinsConfig,
  JenkinsConfigRequest,
  MailHogConfig,
  MailHogConfigRequest,
  TakeBackupRequest,
} from '../Types/BackupType';

const BASE = '/admin/backup';

/* ===========================
   DB config
=========================== */

export const getBackupConfigs = () => {
  return getApi<BackupConfig[]>(`${BASE}/configs`);
};

export const createBackupConfig = (payload: BackupConfigRequest) => {
  return postApi<BackupConfig>(`${BASE}/configs`, payload);
};

export const updateBackupConfig = (id: string, payload: BackupConfigRequest) => {
  return putApi<BackupConfig>(`${BASE}/configs`, id, payload);
};

export const deleteBackupConfig = (id: string) => {
  return deleteApi(`${BASE}/configs`, id);
};

/**
 * Opens a short-lived connection with the stored credentials. The backend responds
 * `success:false` with the driver message when the host or credentials are wrong.
 */
export const testBackupConfig = (id: string) => {
  return postApi<BackupConfig>(`${BASE}/configs/${id}/test`, {});
};

/* ===========================
   Folder
=========================== */

export const getBackupFolderStatus = (configId: string) => {
  return getApi<FolderStatus>(`${BASE}/configs/${configId}/folder-status`);
};

/** Creates the configured folder when it is missing. */
export const setupBackupFolder = (configId: string) => {
  return postApi<FolderStatus>(`${BASE}/configs/${configId}/setup-folder`, {});
};

/* ===========================
   Runs
=========================== */

/**
 * Paginated history with the tab's filters applied server-side. Blank strings are omitted so
 * the backend treats them as "no filter" rather than an empty match.
 */
export const getBackupJobs = (params?: BackupJobFilters) => {
  const clean: Record<string, unknown> = {
    page: params?.page ?? 0,
    size: params?.size ?? 20,
  };
  const fields: (keyof BackupJobFilters)[] = [
    'backupType',
    'environment',
    'status',
    'format',
    'search',
    'fromDate',
    'toDate',
  ];
  fields.forEach((field) => {
    const value = params?.[field];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      clean[field] = String(value).trim();
    }
  });
  return getApi<PaginationResponse<BackupJob>>(`${BASE}/jobs`, clean);
};

export const getBackupJob = (id: string) => {
  return getApi<BackupJob>(`${BASE}/jobs/${id}`);
};

/**
 * Tables of a configured database, used by the pre-run table picker. The backend answers with
 * `success:false` when the connection cannot be opened.
 */
export const getBackupTables = (configId: string) => {
  return getApi<string[]>(`${BASE}/configs/${configId}/tables`);
};

/**
 * Stops an in-flight run. The worker flips the job to CANCELLED at its next checkpoint; the
 * already-written tables remain in the folder.
 */
export const cancelBackup = (id: string) => {
  return postApi<BackupJob>(`${BASE}/jobs/${id}/cancel`, {});
};

/**
 * Starts a run. Returns as soon as the job row exists as RUNNING; the export continues on the
 * server. Poll the list while a run is in flight.
 */
export const takeBackup = (payload: TakeBackupRequest) => {
  return postApi<BackupJob>(`${BASE}/take`, payload);
};

export const deleteBackupJob = (id: string) => {
  return deleteApi(`${BASE}/jobs`, id);
};

/* ===========================
   Jenkins source
=========================== */

export const getJenkinsConfigs = () => {
  return getApi<JenkinsConfig[]>(`${BASE}/jenkins-configs`);
};

export const createJenkinsConfig = (payload: JenkinsConfigRequest) => {
  return postApi<JenkinsConfig>(`${BASE}/jenkins-configs`, payload);
};

export const updateJenkinsConfig = (id: string, payload: JenkinsConfigRequest) => {
  return putApi<JenkinsConfig>(`${BASE}/jenkins-configs`, id, payload);
};

export const deleteJenkinsConfig = (id: string) => {
  return deleteApi(`${BASE}/jenkins-configs`, id);
};

/** Calls the Jenkins API with the stored credentials; backend responds success:false on failure. */
export const testJenkinsConfig = (id: string) => {
  return postApi<JenkinsConfig>(`${BASE}/jenkins-configs/${id}/test`, {});
};

export const setupJenkinsFolder = (configId: string) => {
  return postApi<FolderStatus>(`${BASE}/jenkins-configs/${configId}/setup-folder`, {});
};

/** Starts a Jenkins run for the given config; poll the jobs list while it runs. */
export const takeJenkinsBackup = (configId: string) => {
  return postApi<BackupJob>(`${BASE}/take-jenkins/${configId}`, {});
};

/* ===========================
   MailHog source
=========================== */

export const getMailHogConfigs = () => {
  return getApi<MailHogConfig[]>(`${BASE}/mailhog-configs`);
};

export const createMailHogConfig = (payload: MailHogConfigRequest) => {
  return postApi<MailHogConfig>(`${BASE}/mailhog-configs`, payload);
};

export const updateMailHogConfig = (id: string, payload: MailHogConfigRequest) => {
  return putApi<MailHogConfig>(`${BASE}/mailhog-configs`, id, payload);
};

export const deleteMailHogConfig = (id: string) => {
  return deleteApi(`${BASE}/mailhog-configs`, id);
};

/** Calls the MailHog API with the stored URL; backend responds success:false on failure. */
export const testMailHogConfig = (id: string) => {
  return postApi<MailHogConfig>(`${BASE}/mailhog-configs/${id}/test`, {});
};

export const setupMailHogFolder = (configId: string) => {
  return postApi<FolderStatus>(`${BASE}/mailhog-configs/${configId}/setup-folder`, {});
};

/** Starts a MailHog run for the given config; poll the jobs list while it runs. */
export const takeMailHogBackup = (configId: string) => {
  return postApi<BackupJob>(`${BASE}/take-mailhog/${configId}`, {});
};

/**
 * Streams a run's ZIP into the browser so the backup can also be kept off the server.
 *
 * Fetched through ApiClient rather than getApi so the Authorization header and the 401 refresh
 * still apply; on a failure the backend's JSON error arrives wrapped in a Blob and has to be
 * unwrapped before it can be shown, which is why the message is normalised here.
 *
 * `fallbackName` carries the run's start time and id so even when the server omits the
 * Content-Disposition header the saved file is still recognisable.
 */
export const downloadBackup = async (jobId: string, fallbackName?: string) => {
  try {
    const response = await ApiClient.get(`${BASE}/jobs/${jobId}/download`, {
      responseType: 'blob',
    });

    const blob = response.data as Blob;
    const disposition = String(response.headers?.['content-disposition'] || '');
    const named = disposition.match(/filename="?([^";]+)"?/);
    saveBlob(blob, named?.[1] || fallbackName || `nexgent-backup-${jobId}.zip`);
  } catch (error: any) {
    const raw = error?.response?.data;
    let message = 'Could not download the backup.';

    if (raw instanceof Blob) {
      try {
        const parsed = JSON.parse(await raw.text());
        message = parsed?.message || message;
      } catch {
        // Not JSON - keep the generic message.
      }
    } else if (raw?.message) {
      message = raw.message;
    } else if (error?.message) {
      message = error.message;
    }

    throw new Error(message);
  }
};

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick so Firefox has time to start the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
